import { useState, useEffect } from 'react';
import { getISOWeek } from 'date-fns';
import { initializeDB } from '../config/db';
import { WEEK_LIST_STORE, EMPLOYEE_STORE } from '../config/constants';

export const useCurrentWeekRosterEmployees = () => {
    const [rosterEmployees, setRosterEmployees] = useState([]);
    const [hasRoster, setHasRoster] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadCurrentWeekRoster = async () => {
        setLoading(true);
        try {
            const db = await initializeDB();
            const currentWeekNumber = getISOWeek(new Date());
            const currentYear = new Date().getFullYear();
            const weekYear = `${currentYear}-W${String(currentWeekNumber).padStart(2, '0')}`;
            
            const tx = db.transaction(WEEK_LIST_STORE, 'readonly');
            const store = tx.objectStore(WEEK_LIST_STORE);
            const currentList = await store.get(weekYear);
            await tx.done;

            if (currentList) {
                const employeeIds = [];
                
                // Handle both new format (permanentEmployees/temporaryEmployees) and old format (employees)
                if (currentList.permanentEmployees || currentList.temporaryEmployees) {
                    // New format
                    if (currentList.permanentEmployees) {
                        employeeIds.push(...currentList.permanentEmployees);
                    }
                    if (currentList.temporaryEmployees) {
                        employeeIds.push(...currentList.temporaryEmployees);
                    }
                } else if (currentList.employees) {
                    // Old format
                    employeeIds.push(...currentList.employees);
                }
                
                if (employeeIds.length > 0) {
                    // Now fetch the actual employee data to get names
                    const employeeTx = db.transaction(EMPLOYEE_STORE, 'readonly');
                    const employeeStore = employeeTx.objectStore(EMPLOYEE_STORE);
                    
                    const employeePromises = employeeIds.map(id => {
                        // Handle both number and string IDs
                        const employeeId = typeof id === 'object' && id.id ? id.id : id;
                        return employeeStore.get(Number(employeeId)).catch(() => null);
                    });
                    
                    const employeeRecords = await Promise.all(employeePromises);
                    await employeeTx.done;
                    
                    // Filter out nulls and extract names
                    const employeesWithNames = employeeRecords
                        .filter(emp => emp !== null && emp !== undefined)
                        .map(emp => {
                            // Safe property access
                            const id = emp?.id || 0;
                            const fullName = emp?.fullName || 
                                           `${emp?.firstName || ''} ${emp?.lastName || ''}`.trim() || 
                                           emp?.name || 
                                           'Unknown';
                            return { id, fullName };
                        });
                    
                    console.log('Weekly roster employees with names:', employeesWithNames);
                    setRosterEmployees(employeesWithNames);
                    setHasRoster(true);
                } else {
                    setRosterEmployees([]);
                    setHasRoster(false);
                }
            } else {
                setRosterEmployees([]);
                setHasRoster(false);
            }
        } catch (error) {
            console.error('Error loading current week roster employees:', error);
            setRosterEmployees([]);
            setHasRoster(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCurrentWeekRoster();

        // Listen for roster updates
        const handleRosterUpdate = () => {
            loadCurrentWeekRoster();
        };

        window.addEventListener('weeklyRosterUpdated', handleRosterUpdate);
        
        return () => {
            window.removeEventListener('weeklyRosterUpdated', handleRosterUpdate);
        };
    }, []);

    return {
        rosterEmployees,
        hasRoster,
        loading
    };
};